import React, { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { withCookies } from "react-cookie";
// import Header from "./Header";
import MainContent from "./MainContent";
import ContactHelp from "./ContactHelp";
import Alert from "./Alert";
import { FormInputButton } from "./Button";
import PasswordRequirements from "./PasswordRequirements";
import PasswordField from "./PasswordField";
import DMS_1997 from "../services/PasswordQualityService";
import Loading from "./Loading";
import { FadeUp } from "./AnimationLibrary";

// If these numbers are needed outside of this Component, they should be exported
const VERIFICATION_CODE_LIFETIME =  30 * (60 * 1000); // 30 minutes * (60 seconds per minute * 1000 milliseconds per second) 
const PAGE_TIMEOUT = VERIFICATION_CODE_LIFETIME - (30 * 1000) // show user timeout message 30 seconds before code actually expires

// If these lists are needed outside of this Component (e.g. for user messaging), they should be exported
const lettersAndNumbers = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"
const simpleSymbols = "~!@#$%^&*()-_=+[]{}|;:,./<>";
const trickySymbols = "`'\\\"";	// [grave accent, apostrophe, backslash, double-quote] - possibly tricky in Javascript, don't change the string willy nilly and break it
const whitespace = " "; //single space only
const allValidPasswordCharacters = lettersAndNumbers + simpleSymbols + trickySymbols + whitespace;

const SetPassword = props => {
	const minLength = DMS_1997.minLength;
	const maxLength = DMS_1997.maxLength;
	// const csrfToken = cookies.get("XSRF-TOKEN");

	// Setting and updating state of UI
	const [uiState, setUiState] = useState({
		success: false,
		showPassword: false,
		password: "",
		confirmedPassword: "",
		quality: DMS_1997.checkPasswordQuality("", []),
		alertState: {},
		loading: false
	});

	// Component state related to timeout of page
	const [routeToVerification, setRouteToVerification] = useState(false);
	const [submissionAttempted, setSubmissionAttempted] = useState(false);
	const [timerFinished, setTimerFinished] = useState(false);

	const { success, showPassword, password, confirmedPassword, quality, alertState, loading } = uiState;

	const handleCloseAlert = () => {
		setUiState({ ...uiState, alertState: {} });
	};

	// Password verification
	const readyToConfirm = quality.adequateLength ? true : false;
	const passwordsMatch = confirmedPassword && password === confirmedPassword ? true : false;
	const readyToSet = readyToConfirm && passwordsMatch ? true : false;

	const checkForValidCharacters = (password) => { 
		const pwArray = [...password];
		const allowedArray = [...allValidPasswordCharacters];

		return pwArray.every(x => allowedArray.includes(x));
	}

	const validCharacters = checkForValidCharacters(password);

	const handleSetPw = async () => {
		if (password.length > maxLength) {
			setUiState({
				...uiState,
				alertState: {
					title: "Sorry, that password is too long",
					message: `Please keep it under ${maxLength} characters and try again.`
				}
			});
			return;
		} 

		if (validCharacters === false) {
			setUiState({
				...uiState,
				alertState: {
					title: "Looks like your password contains invalid characters",
					message: "Please use only standard US English keyboard characters and try again.",
					messageDetail: allValidPasswordCharacters.split("").join(" "),
					collapseText: "Hide valid characters list",
					expandText: "Show valid characters list"
				}
			});
			return;
		}

		setUiState({ ...uiState, loading: true });
		const result = await props.apiService.change_password(props.user.netid, password, props.token);
		// TODO - there may be a stale state problem here, since 'uiState' was captured before the (slow) api call
		// https://dmitripavlutin.com/react-usestate-hook-guide/#42-stale-state
		if (!result.ok) {
			setUiState({
				...uiState,
				loading: false,
				alertState: {
					title: "Failed to set password",
					message: "Sorry, we had an issue while trying to set your password. Please try again.",
					messageDetail: JSON.stringify(result, null, 2)
				}
			});
			return;
		} 
		
		setUiState({ ...uiState, success: true });
	};

	const handleTogglePasswordVisibility = () => {
		setUiState({ ...uiState, showPassword: !showPassword });
	};

	const handlePasswordChange = password => {
		const userSpecificDenyList = [`${props.user.netid}`, `${props.user.firstName}`];
		const quality = DMS_1997.checkPasswordQuality(password, userSpecificDenyList, minLength);
		setUiState({ ...uiState, password: password, quality: quality });
	};

	const handleConfirmedPasswordChange = confirmedPassword => {
		setUiState({ ...uiState, confirmedPassword });
	};

	const handleSubmit = e => {
		e.preventDefault();
		setSubmissionAttempted(true);
		handleSetPw();
	};

	useEffect(() => {
		const timeoutMessage = {
			title: "Sorry, the time for setting your password has expired",
			message: "Maybe it wasn't a good time. If you're ready to set your password now, let's verify your identity and try again.",
			buttonTextPrimary: "Verify my identity",
			onClickPrimary: () => { setRouteToVerification(true) },
			dismissable: false
		};
		const timer = setTimeout(() => {
			setTimerFinished(true);
			setUiState({
				...uiState,
				alertState: submissionAttempted ? {} : timeoutMessage
			});
		}, PAGE_TIMEOUT);

		return () => { clearTimeout(timer); };
		// eslint-disable-next-line
	}, [submissionAttempted]);

	// note different 'success' pages for new user vs. password reset
	if (success) return <Redirect push from="/setpassword" to={props.pwReset ? "/success" : "/confirmation"} />;
	if (!props.token) return <Redirect push from="/setpassword" to="/" />;
	if (routeToVerification) return <Redirect psuh from="/setpassword" to={props.user ? "/selectverificationoption" : "/"} />;

	return (
		<>
			{alertState.message && <Alert {...uiState.alertState} close={handleCloseAlert} />}
			{loading && <Loading loadingMessage="Setting password..." />}

			<MainContent user={props.user} showStepProgress={!props.pwReset} step="2" sideBar={true}>
				<div
					className={`main-message-container
											${!quality.adequateLength ? "min-length-message" : ""}
											${quality.adequateLength && !passwordsMatch ? "passwords-match-message" : ""}`}
				>
					<form onSubmit={handleSubmit}>
						<FadeUp delay="0">
							<PasswordField
								label="Create a Password"
								id="create-password"
								password={password}
								onPasswordChange={handlePasswordChange}
								minLength={minLength}
								showStrengthMeter={true}
								score={quality.score}
								isFocused={true}
								showPassword={showPassword}
								togglePasswordVisibility={handleTogglePasswordVisibility}
								disableField={false || timerFinished}
								passwordsMatch={passwordsMatch}
							/>
							<PasswordRequirements
								minLength={minLength}
								adequateLength={quality.adequateLength}
								passwordsMatch={passwordsMatch}
								showAdequateLength="true"
							/>
						</FadeUp>
						<FadeUp delay="150">
							<PasswordField
								label="Confirm Password"
								id="confirm-password"
								password={confirmedPassword}
								onPasswordChange={handleConfirmedPasswordChange}
								minLength={minLength}
								showStrengthMeter={false}
								isFocused={false}
								showPassword={showPassword}
								togglePasswordVisibility={handleTogglePasswordVisibility}
								disableField={!readyToConfirm || timerFinished}
								passwordsMatch={passwordsMatch}
							/>
							<PasswordRequirements
								minLength={minLength}
								password={password}
								confirmedPassword={confirmedPassword}
								adequateLength={quality.adequateLength}
								passwordsMatch={passwordsMatch}
								showPasswordsMatch="true"
							/>
						</FadeUp>
						<FadeUp delay="300">
							<FormInputButton
								label={!loading ? "Set Password" : "Setting Password..."}
								disabled={!readyToSet || loading || alertState.message}
								inputClassNames={`btn ${!quality.adequateLength ? "min-length-message" : ""} ${
									quality.adequateLength && !passwordsMatch ? "passwords-match-message" : ""
								}`}
								dataTestId="set-password"
							/>
						</FadeUp>
					</form>
				</div>
			</MainContent>
			<ContactHelp />
		</>
	);
};

export default withCookies(SetPassword);
